import { api } from '../client';

export type RentalStatus = 'ON_HIRE' | 'DUE_SOON' | 'OVERDUE' | 'IDLE' | 'OPEN_ENDED' | 'OFF_HIRE';

export interface Rental {
  id: string;
  /** The agreement or PO number in the ERP — the key back to the record. */
  externalReference: string;
  description: string;
  manufacturer: string | null;
  model: string | null;
  /** The lessor's unit or serial number. Theirs, not ours. */
  serialNumber: string | null;
  /** The lessor's own ticket or contract number, as distinct from our PO. */
  lessorReference: string | null;
  lessor: string;
  jobId: string | null;
  jobNumber: string | null;
  jobName: string | null;
  jobStatus: string | null;
  wellName: string | null;
  locationName: string | null;
  onHireAt: string;
  expectedOffHireAt: string | null;
  offHireAt: string | null;
  /** As recorded from the agreement. Not calculated here. */
  dayRate: number | null;
  currency: string;
  status: RentalStatus;
  daysOnHire: number;
  daysOverdue: number | null;
  daysIdle: number | null;
  statement: string;
  needsAction: boolean;
}

export interface RentalSummary {
  onHire: number;
  idle: number;
  overdue: number;
  openEnded: number;
  dueSoon: number;
  needsAction: number;
}

/** One rental with everything that identifies the physical unit. */
export interface RentalDetail extends Rental {
  lessorName: string | null;
  notes: string | null;
  supplier: {
    id: string;
    name: string;
    code: string;
    contactName: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  location: { id: string; name: string } | null;
  /** Set only where we also carry the tool in our own register. */
  equipment: { id: string; assetNumber: string; name: string } | null;
  job: {
    id: string;
    jobNumber: string;
    name: string;
    status: string;
    afeNumber: string | null;
    customerName: string | null;
    well: { id: string; name: string; apiNumber: string } | null;
  } | null;
}

export const rentalsApi = {
  async getAll(includeReturned = false) {
    const response = await api.get<Rental[]>('/equipment/rentals', {
      params: includeReturned ? { includeReturned: 'true' } : undefined,
    });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<RentalDetail>(`/equipment/rentals/${id}`);
    return response.data;
  },

  async getSummary() {
    const response = await api.get<RentalSummary>('/equipment/rentals/summary');
    return response.data;
  },

  async recordOffHire(rentalId: string, offHireAt?: string) {
    const response = await api.patch(`/equipment/rentals/${rentalId}/off-hire`, { offHireAt });
    return response.data;
  },
};
